import { useId, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { CircleAlert, Lock, SearchX, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { DEFAULT_FORBIDDEN_TITLE } from "./pageStateAnnouncement";

/**
 * A state's action: a route (`href`) or a handler (`onClick`). Exactly one, so it always does
 * something. A route can carry router `state` onto the entry it opens (the way back to a list's
 * scroll position, say).
 */
export type PageStateAction = { label: string } & (
  | { href: string; onClick?: never; state?: unknown }
  | { onClick: () => void; href?: never; state?: never }
);

export type PageStateKind = "loading" | "empty" | "filtered" | "error" | "forbidden";

interface PageStateCommon {
  className?: string;
  /**
   * The state's heading level. It sits under the page's h1, so 2 for a page's content area and
   * 3 inside a panel or tab (whose section already has an h2).
   */
  headingLevel?: 2 | 3 | 4;
}

/**
 * The five states every template renders (docs/design/NAVIGATION_AND_STATES.md § Page states).
 * Each kind requires what the spec requires of it: an error offers Try again (or, where no
 * retry could help, a way out), a filtered state offers Clear filters, and a no-permission
 * state names the permission.
 */
export type PageStateProps = PageStateCommon & (
  | {
    kind: "loading";
    /** The template's own skeleton, matching the final layout. No spinners. */
    skeleton: ReactNode;
    /** What is loading, for screen readers, e.g. "Loading people". */
    label?: string;
  }
  | {
    /** First run: the list has no records at all. Never use it while filters are set. */
    kind: "empty";
    title: string;
    /** What this page is for. */
    description: string;
    /** The one gold action. Omit it for viewers who can't act. */
    action?: PageStateAction;
    /** A quieter second way in, e.g. "Import". */
    secondaryAction?: PageStateAction;
  }
  | {
    /** Filters hide every record. */
    kind: "filtered";
    /** e.g. "No people match these filters". */
    title: string;
    description?: string;
    onClearFilters: () => void;
    clearLabel?: string;
  }
  | ({
    kind: "error";
    /** What failed, e.g. "Couldn't load people". */
    title: string;
    /** Whether data is safe. Defaults to saying nothing was changed. */
    description?: string;
  } & (
    | { onRetry: () => void; secondaryAction?: PageStateAction }
    /**
     * Nothing a retry could change (a record that isn't there): no Try again, so a way out
     * instead, and its own description, since the default one suggests trying again.
     */
    | { onRetry?: never; secondaryAction: PageStateAction; description: string }
  ))
  | {
    kind: "forbidden";
    /** The permission the viewer lacks, as an admin would find it, e.g. "View people". */
    permission: string;
    /** Who can grant it. Defaults to an organization admin. */
    grantedBy?: string;
    title?: string;
    /**
     * Sends an access request. Omitted until requests have somewhere to go (#7010): the state
     * then names who can grant the permission, with no button that does nothing.
     */
    onRequestAccess?: () => void;
  }
);

type Scale = "page" | "panel";

const DEFAULT_ERROR_DESCRIPTION = "Nothing was changed. Try again, or check back in a few minutes.";
/** For an error with no Try again, should one reach the page without its own description. */
const DEFAULT_NO_RETRY_DESCRIPTION = "Nothing was changed.";

// 44×44px targets below 1024px, 24×24px above (ACCESSIBILITY.md § Target size).
const ACTION_TARGETS =
  "[&_a]:min-h-11 [&_a]:min-w-11 [&_button]:min-h-11 [&_button]:min-w-11 lg:[&_a]:min-h-6 lg:[&_a]:min-w-6 lg:[&_button]:min-h-6 lg:[&_button]:min-w-6";

function StateAction({ action, variant }: { action: PageStateAction; variant: "primary" | "outline" }) {
  if (action.href !== undefined) {
    return (
      <Button asChild variant={variant} size="sm">
        <Link to={action.href} state={action.state}>{action.label}</Link>
      </Button>
    );
  }
  return <Button type="button" variant={variant} size="sm" onClick={action.onClick}>{action.label}</Button>;
}

function StateView({ scale, ...props }: PageStateProps & { scale: Scale }) {
  const headingId = useId();
  const page = scale === "page";
  const level = props.headingLevel ?? (page ? 2 : 3);
  const Heading = `h${level}` as "h2" | "h3" | "h4";

  if (props.kind === "loading") {
    return (
      <div className={props.className}>
        {/* Beside the busy region, not inside it: assistive tech holds back live updates within
            an aria-busy subtree, which would silence this label. */}
        <span role="status" className="sr-only">{props.label ?? "Loading"}</span>
        <div aria-busy="true">{props.skeleton}</div>
      </div>
    );
  }

  const Icon = props.kind === "empty"
    ? Sparkles
    : props.kind === "filtered"
      ? SearchX
      : props.kind === "error"
        ? CircleAlert
        : Lock;

  let title: string;
  let body: ReactNode;
  let actions: ReactNode = null;
  switch (props.kind) {
    case "empty":
      title = props.title;
      body = props.description;
      actions = props.action || props.secondaryAction
        ? (
          <>
            {props.action ? <StateAction action={props.action} variant="primary" /> : null}
            {props.secondaryAction ? <StateAction action={props.secondaryAction} variant="outline" /> : null}
          </>
        )
        : null;
      break;
    case "filtered":
      title = props.title;
      body = props.description;
      actions = (
        <Button type="button" variant="outline" size="sm" onClick={props.onClearFilters}>
          {props.clearLabel ?? "Clear filters"}
        </Button>
      );
      break;
    case "error":
      title = props.title;
      body = props.description
        ?? (props.onRetry !== undefined ? DEFAULT_ERROR_DESCRIPTION : DEFAULT_NO_RETRY_DESCRIPTION);
      actions = (
        <>
          {props.onRetry !== undefined
            ? <Button type="button" variant="outline" size="sm" onClick={props.onRetry}>Try again</Button>
            : null}
          {props.secondaryAction ? <StateAction action={props.secondaryAction} variant="outline" /> : null}
        </>
      );
      break;
    case "forbidden":
      title = props.title ?? DEFAULT_FORBIDDEN_TITLE;
      body = (
        <>
          Your role doesn't include <strong className="font-semibold text-foreground">{props.permission}</strong>.{" "}
          {props.grantedBy ?? "An organization admin"} can give it to you.
        </>
      );
      actions = props.onRequestAccess
        ? <Button type="button" variant="outline" size="sm" onClick={props.onRequestAccess}>Request access</Button>
        : null;
      break;
  }

  return (
    <section
      aria-labelledby={headingId}
      data-state={props.kind}
      data-scale={scale}
      className={cn(
        page
          ? "flex flex-col items-center rounded-[var(--card-radius)] border border-dashed border-hairline px-6 py-12 text-center"
          : "flex flex-col items-start rounded-[var(--card-radius)] border border-dashed border-hairline px-4 py-5",
        // First run uses the module accent tint (NAVIGATION_AND_STATES.md); the rest stay neutral.
        props.kind === "empty"
          ? "border-[var(--module-border,hsl(var(--border)))] bg-[var(--module-tint,hsl(var(--muted)))]"
          : "bg-card",
        props.className,
      )}
    >
      <div
        aria-hidden="true"
        className={cn(
          "flex shrink-0 items-center justify-center rounded-full",
          page ? "size-11" : "size-8",
          props.kind === "empty"
            ? "bg-background text-[var(--module-color,hsl(var(--muted-foreground)))]"
            : props.kind === "error"
              ? "bg-destructive/10 text-destructive"
              : "bg-muted text-muted-foreground",
        )}
      >
        <Icon className={page ? "size-5" : "size-4"} />
      </div>
      <Heading
        id={headingId}
        className={cn(
          "text-foreground",
          page ? "mt-4 font-display text-section-title" : "mt-3 text-sm font-semibold",
        )}
      >
        {title}
      </Heading>
      {body
        ? (
          <p className={cn("mt-1.5 text-sm text-muted-foreground", page && "mx-auto max-w-md")}>
            {body}
          </p>
        )
        : null}
      {actions
        ? (
          <div className={cn("mt-4 flex flex-wrap items-center gap-2", page && "justify-center", ACTION_TARGETS)}>
            {actions}
          </div>
        )
        : null}
    </section>
  );
}

/**
 * A template's content area in one of its five states: loading, first-run empty, filtered,
 * error or no permission (docs/design/NAVIGATION_AND_STATES.md § Page states). Templates place
 * it (T1 keeps its header and views; T2 keeps its hero and tabs); pages choose the kind.
 *
 * Copy is plain and calm: say what happened and what to do, never "Oops", never blame the
 * viewer. An error says whether data is safe.
 *
 * @example
 * <PageState kind="filtered" title="No people match these filters" onClearFilters={clear} />
 * <PageState kind="forbidden" permission="View people" />
 */
export function PageState(props: PageStateProps) {
  return <StateView {...props} scale="page" />;
}

/**
 * The same five states at panel scale, for a T2 tab or a T3 panel: left-aligned, compact, and
 * headed at h3 by default. A failing panel shows this while the rest of the page keeps what
 * loaded (fail per panel, not per page).
 *
 * @example
 * <PanelState kind="error" title="Couldn't load giving" onRetry={refetch} />
 */
export function PanelState(props: PageStateProps) {
  return <StateView {...props} scale="panel" />;
}
